import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

function WarningTriangle({ color, size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                fill={hexToRgba(color, 0.15)}
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <line x1="12" y1="9" x2="12" y2="13" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="16.5" r="0.8" fill={color} />
        </svg>
    );
}

const WARN_RED = '#ef4444';
const WARN_AMBER = '#f59e0b';

export default function ProblemGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const variant = config.variant || 'cards';
    const items = content.items || [];

    if (items.length === 0) return null;

    const columns = config.columns || 4;
    const cols = columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const warnColor = s.dark ? WARN_AMBER : WARN_RED;

    // ── Cards: warning-accented cards with border-left ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => (
                            <div
                                key={i}
                                className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg`}
                                style={{
                                    borderLeft: `4px solid ${hexToRgba(warnColor, 0.7)}`,
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                    transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms, box-shadow 0.3s ease`,
                                }}
                            >
                                <div className="p-5">
                                    {/* Icon circle */}
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                                        style={{ backgroundColor: hexToRgba(warnColor, 0.1), border: `1px solid ${hexToRgba(warnColor, 0.2)}` }}
                                    >
                                        {item.icon ? (
                                            <SectionIcon name={item.icon} size={22} color={warnColor} />
                                        ) : (
                                            <WarningTriangle color={warnColor} size={22} />
                                        )}
                                    </div>
                                    {item.title && (
                                        <h3 className="text-base font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                    )}
                                    {item.description && (
                                        <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Icons: compact grid, large icon centered, minimal ──
    if (variant === 'icons') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-8`}>
                        {items.map((item, i) => (
                            <div
                                key={i}
                                className="group text-center transition-all duration-500"
                                style={{
                                    opacity: visible ? 1 : 0,
                                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                                    transition: `opacity 0.5s ease ${i * 90}ms, transform 0.5s ease ${i * 90}ms`,
                                }}
                            >
                                {/* Large icon */}
                                <div
                                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                                    style={{
                                        backgroundColor: hexToRgba(WARN_AMBER, 0.1),
                                        border: `1.5px solid ${hexToRgba(WARN_AMBER, 0.2)}`,
                                    }}
                                >
                                    {item.icon ? (
                                        <SectionIcon name={item.icon} size={28} color={WARN_AMBER} />
                                    ) : (
                                        <WarningTriangle color={WARN_AMBER} size={26} />
                                    )}
                                </div>
                                {item.title && (
                                    <h3 className="text-sm font-bold mb-1.5" style={{ color: s.text }}>{item.title}</h3>
                                )}
                                {item.description && (
                                    <p className="text-xs leading-relaxed max-w-[220px] mx-auto" style={{ color: s.textSub }}>{item.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Numbered: sequential problems with large numbers ──
    if (variant === 'numbered') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {items.map((item, i) => {
                            const num = String(i + 1).padStart(2, '0');
                            return (
                                <div
                                    key={i}
                                    className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg`}
                                    style={{
                                        borderTop: `3px solid ${hexToRgba(warnColor, 0.6)}`,
                                        opacity: visible ? 1 : 0,
                                        transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                        transition: `opacity 0.6s ease ${i * 120}ms, transform 0.6s ease ${i * 120}ms, box-shadow 0.3s ease`,
                                    }}
                                >
                                    <div className="p-5">
                                        {/* Large number */}
                                        <span
                                            className="text-4xl font-black block mb-3 transition-colors duration-300"
                                            style={{ color: hexToRgba(warnColor, 0.25) }}
                                        >
                                            {num}
                                        </span>
                                        {item.icon && (
                                            <div className="mb-2" style={{ color: warnColor }}>
                                                <SectionIcon name={item.icon} size={22} color={warnColor} />
                                            </div>
                                        )}
                                        {item.title && (
                                            <h3 className="text-base font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                        )}
                                        {item.description && (
                                            <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Alert: warning-box styled cards ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${cols} gap-5`}>
                    {items.map((item, i) => (
                        <div
                            key={i}
                            className="group rounded-xl overflow-hidden transition-all duration-500 hover:shadow-md"
                            style={{
                                backgroundColor: s.dark ? hexToRgba(WARN_AMBER, 0.06) : hexToRgba(WARN_AMBER, 0.05),
                                border: `1px solid ${hexToRgba(WARN_AMBER, 0.18)}`,
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                                transition: `opacity 0.5s ease ${i * 100}ms, transform 0.5s ease ${i * 100}ms, box-shadow 0.3s ease`,
                            }}
                        >
                            <div className="p-5">
                                <div className="flex items-start gap-3">
                                    {/* Warning triangle */}
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                                        style={{ backgroundColor: hexToRgba(WARN_AMBER, 0.12) }}
                                    >
                                        {item.icon ? (
                                            <SectionIcon name={item.icon} size={20} color={WARN_AMBER} />
                                        ) : (
                                            <WarningTriangle color={WARN_AMBER} size={20} />
                                        )}
                                    </div>
                                    <div>
                                        {item.title && (
                                            <h3 className="text-sm font-bold mb-1" style={{ color: s.text }}>{item.title}</h3>
                                        )}
                                        {item.description && (
                                            <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
